import { useEffect, useMemo, useState } from "react";
import Modal from "react-modal";
import { useNavigate } from "react-router";
import Confetti from "react-confetti";
import { FiDownload, FiArrowRight, FiX, FiEye, FiEyeOff } from "react-icons/fi";
import { LuUsers, LuStore } from "react-icons/lu";

import * as XLSX from "xlsx-js-style";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

/* English property key -> Albanian column header */
const COLUMN_LABELS = {
  user_id: "ID",
  id: "ID",
  first_name: "Emri",
  last_name: "Mbiemri",
  email: "Email",
  phone_number: "Telefoni",
  personal_number: "Numri personal",
  tier_level: "Kushti",
  prize_amount: "Shuma fituese",
  receipt_id: "Kuponi",
  receipt_count: "Kuponat",
  amount_count: "Totali",
  cashback_amount: "Cashback",
  business_nui: "NUI",
  business_name: "Biznesi",
  fiscal_number: "Numri fiskal",
  municipality_no: "Komuna",
  total_amount: "Totali",
  prize_desc: "Shpërblimi",
};

const labelFor = (key) => COLUMN_LABELS[key] || key;

const collectProps = (rows) => {
  /* Keep the row's own key order (first_name, last_name, ...) even when the
     first rows have an empty value for a key; only drop keys empty everywhere */
  const allProps = new Set();
  const filledProps = new Set();
  rows.forEach((r) =>
    Object.keys(r).forEach((k) => {
      allProps.add(k);
      if (r[k] !== null && r[k] !== undefined && r[k] !== "") filledProps.add(k);
    })
  );
  allProps.forEach((k) => !filledProps.has(k) && allProps.delete(k));
  /* Dedupe columns that resolve to the same Albanian label (e.g. user_id + id -> "ID") */
  const seenLabels = new Set();
  return Array.from(allProps).filter((k) => {
    const label = labelFor(k);
    if (seenLabels.has(label)) return false;
    seenLabels.add(label);
    return true;
  });
};

const exportWinnersToExcel = (rows, title) => {
  if (!rows?.length) {
    alert("Nuk ka të dhëna për të gjeneruar");
    return;
  }
  const props = collectProps(rows);
  const formatted = rows.map((r, i) => {
    const out = { "#": i + 1 };
    props.forEach((p) => {
      if (r[p] !== null && r[p] !== undefined && r[p] !== "") out[labelFor(p)] = r[p];
    });
    return out;
  });
  const ws = XLSX.utils.json_to_sheet(formatted);
  const cols = Object.keys(formatted[0] || {});
  ws["!cols"] = cols.map((k) => ({
    wch:
      Math.max(
        k.length,
        ...formatted.map((row) => (row[k] ? row[k].toString().length : 0))
      ) + 2,
  }));

  /* Styling to match the PDF look */
  const headerStyle = {
    font: { bold: true, color: { rgb: "FFFFFF" }, sz: 11 },
    fill: { fgColor: { rgb: "374151" } },
    alignment: { horizontal: "center", vertical: "center" },
    border: {
      top: { style: "thin", color: { rgb: "E5E7EB" } },
      bottom: { style: "thin", color: { rgb: "E5E7EB" } },
      left: { style: "thin", color: { rgb: "E5E7EB" } },
      right: { style: "thin", color: { rgb: "E5E7EB" } },
    },
  };
  const cellBorder = {
    top: { style: "thin", color: { rgb: "F3F4F6" } },
    bottom: { style: "thin", color: { rgb: "F3F4F6" } },
    left: { style: "thin", color: { rgb: "F3F4F6" } },
    right: { style: "thin", color: { rgb: "F3F4F6" } },
  };
  const range = XLSX.utils.decode_range(ws["!ref"]);
  for (let R = range.s.r; R <= range.e.r; R++) {
    for (let C = range.s.c; C <= range.e.c; C++) {
      const cell = ws[XLSX.utils.encode_cell({ r: R, c: C })];
      if (!cell) continue;
      if (R === 0) {
        cell.s = headerStyle;
      } else {
        cell.s = {
          alignment: { vertical: "center" },
          fill: { fgColor: { rgb: R % 2 === 0 ? "F9FAFB" : "FFFFFF" } },
          border: cellBorder,
        };
      }
    }
  }
  ws["!rows"] = [{ hpt: 22 }];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, title);
  XLSX.writeFile(wb, `${title}.xlsx`);
};

const exportWinnersToPdf = (rows, title, isBusiness) => {
  if (!rows?.length) {
    alert("Nuk ka të dhëna për të gjeneruar");
    return;
  }
  const props = collectProps(rows);
  const head = [["#", ...props.map(labelFor)]];
  const body = rows.map((r, i) => [
    i + 1,
    ...props.map((p) =>
      r[p] !== null && r[p] !== undefined && r[p] !== "" ? String(r[p]) : ""
    ),
  ]);

  const pdf = new jsPDF({ orientation: "landscape" });
  pdf.setFontSize(16);
  pdf.text(isBusiness ? "Lista e Bizneseve Fituese" : "Lista e Fituesve", 14, 12);
  autoTable(pdf, {
    head,
    body,
    startY: 18,
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [55, 65, 81], textColor: 255 },
  });
  pdf.save(`${title}.pdf`);
};

const customStyles = {
  content: {
    position: "static",
    background: "transparent",
    border: "none",
    padding: 0,
    inset: "auto",
  },
  overlay: {
    background: "rgba(0, 0, 0, 0.55)",
    backdropFilter: "blur(4px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 50,
  },
};

if (typeof document !== "undefined" && document.getElementById("root")) {
  Modal.setAppElement(document.getElementById("root"));
}

const isBusinessRecord = (rows = []) => !!rows?.[0]?.business_nui;

export function maskUserData(rows = []) {
  return rows.map((u) => {
    const firstName = u.first_name;
    const lastName = u.last_name;
    const maskedFirst = firstName
      ? firstName.slice(0, 1) + "*".repeat(Math.max(0, firstName.length - 1))
      : "";
    const maskedLast = lastName
      ? lastName.slice(0, 1) + "*".repeat(Math.max(0, lastName.length - 1))
      : "";
    let maskedPN = u.personal_number ? u.personal_number.toString() : "";
    if (maskedPN.length === 10) maskedPN = "*".repeat(7) + maskedPN.slice(-3);
    return { ...u, first_name: maskedFirst, last_name: maskedLast, personal_number: maskedPN };
  });
}

/* businessWinners: the businesses that won alongside a lottery draw's citizens. When given,
   the modal gets "Qytetarët" / "Bizneset" tabs and the list, exports and link follow the tab. */
export default function WinnersModal({
  isOpen,
  handleIsOpen,
  winners: citizenWinners = [],
  businessWinners,
  isBusiness: isBusinessProp,
}) {
  const navigate = useNavigate();
  /* Lottery winner rows carry the receipt's business_nui too, so callers that
     know the game type should say so instead of relying on the guess */
  const isBusinessGame = isBusinessProp ?? isBusinessRecord(citizenWinners);
  const hasTabs = Array.isArray(businessWinners) && !isBusinessGame;
  const [tab, setTab] = useState("citizens");
  const [masked, setMasked] = useState(true);

  const isBusiness = isBusinessGame || (hasTabs && tab === "businesses");
  const winners = useMemo(
    () => (hasTabs && tab === "businesses" ? businessWinners : citizenWinners) || [],
    [hasTabs, tab, businessWinners, citizenWinners]
  );

  const data = useMemo(() => {
    if (!winners || winners.length === 0) return [];
    /* Only citizens are masked — business winners are shown as they are */
    if (!masked || isBusiness) return winners;
    return maskUserData(winners);
  }, [winners, masked, isBusiness]);

  /* Reset to masked (and the first tab) whenever the winners change */
  useEffect(() => {
    setMasked(true);
    setTab("citizens");
  }, [citizenWinners, businessWinners]);

  const handleNavigate = () => {
    handleIsOpen();
    navigate(isBusiness ? "/winners/businesses" : "/winners/individuals");
  };

  const Icon = isBusiness ? LuStore : LuUsers;

  return (
    <Modal isOpen={isOpen} onRequestClose={handleIsOpen} style={customStyles} contentLabel="Winners">
      <Confetti recycle={false} numberOfPieces={220} />
      <div className="bg-white w-full max-w-2xl max-h-[88vh] rounded-2xl shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3 min-w-0">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-[var(--secondary-color)]/10 text-[var(--secondary-color)]">
              <Icon size={18} />
            </span>
            <div className="min-w-0">
              <h2 className="text-base font-bold text-gray-900 truncate">Urime fituesve! 🎉</h2>
              <p className="text-xs text-gray-500">
                {hasTabs
                  ? `${citizenWinners.length.toLocaleString()} qytetarë dhe ${businessWinners.length.toLocaleString()} biznese fituese nga loja`
                  : `${winners.length.toLocaleString()} ${isBusiness ? "biznese fituese" : "fitues të rinj"} nga loja`}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleIsOpen}
            className="inline-flex items-center justify-center w-9 h-9 rounded-lg text-gray-500 hover:bg-gray-100 transition"
            aria-label="Close"
          >
            <FiX size={20} />
          </button>
        </div>

        {hasTabs && (
          <div className="px-5 pt-3 border-b border-gray-100 flex gap-1">
            {[
              { key: "citizens", label: "Qytetarët", count: citizenWinners.length, icon: LuUsers },
              { key: "businesses", label: "Bizneset", count: businessWinners.length, icon: LuStore },
            ].map(({ key, label, count, icon: TabIcon }) => (
              <button
                key={key}
                type="button"
                onClick={() => setTab(key)}
                className={`inline-flex items-center gap-2 px-3 pb-2.5 -mb-px border-b-2 text-sm font-semibold transition ${
                  tab === key
                    ? "border-[var(--secondary-color)] text-gray-900"
                    : "border-transparent text-gray-500 hover:text-gray-800"
                }`}
              >
                <TabIcon size={15} />
                {label}
                <span
                  className={`rounded-full px-1.5 text-[11px] tabular-nums ${
                    tab === key ? "bg-[var(--secondary-color)]/15 text-[var(--secondary-color)]" : "bg-gray-100 text-gray-500"
                  }`}
                >
                  {count}
                </span>
              </button>
            ))}
          </div>
        )}

        {/* Privacy toggle */}
        {!isBusiness && (
        <div className="px-5 py-2.5 border-b border-gray-100 flex items-center justify-between gap-2 bg-gray-50/60">
          <p className="text-xs italic text-gray-500">
            Të dhënat janë të maskuara për shkak të transparencës.
          </p>
          <button
            type="button"
            onClick={() => setMasked((m) => !m)}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-700 hover:text-gray-900 transition"
          >
            {masked ? (
              <>
                <FiEye size={14} />
                Shfaq të dhënat
              </>
            ) : (
              <>
                <FiEyeOff size={14} />
                Fsheh të dhënat
              </>
            )}
          </button>
        </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {data.length === 0 ? (
            <div className="px-5 py-12 text-center text-sm text-gray-500">
              {isBusiness ? "Asnjë biznes nuk u shpall fitues." : "Nuk ka fitues."}
            </div>
          ) : (
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white/95 backdrop-blur">
              <tr className="border-b border-gray-200">
                <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500 w-[10%]">#</th>
                {isBusiness ? (
                  <>
                    <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">Biznesi</th>
                    <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">NUI</th>
                  </>
                ) : (
                  <>
                    <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">Emri</th>
                    <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">Mbiemri</th>
                    <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">ID</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data?.map((item, idx) => (
                <tr key={idx} className="hover:bg-gray-50/70 transition-colors">
                  <td className="px-4 py-2.5 text-gray-500 tabular-nums text-xs">{idx + 1}</td>
                  {isBusiness ? (
                    <>
                      <td className="px-4 py-2.5 font-medium text-gray-900 tracking-wide truncate">
                        {item.business_name || "—"}
                      </td>
                      <td className="px-4 py-2.5 text-gray-700 tabular-nums">{item.business_nui || "—"}</td>
                    </>
                  ) : (
                    <>
                      <td className="px-4 py-2.5 font-medium text-gray-900 tracking-wide">{item.first_name || "—"}</td>
                      <td className="px-4 py-2.5 font-medium text-gray-900 tracking-wide">{item.last_name || "—"}</td>
                      <td className="px-4 py-2.5 text-gray-700 tabular-nums">#{item.id || item.user_id || "—"}</td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-100 flex flex-col gap-2 sm:gap-3">
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            <button
              type="button"
              onClick={() =>
                exportWinnersToExcel(
                  winners,
                  isBusiness ? "Bizneset fituese" : "Fituesit e lotarisë"
                )
              }
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm transition flex-1"
            >
              <FiDownload size={14} />
              Shkarko Excel
            </button>
            <button
              type="button"
              onClick={() =>
                exportWinnersToPdf(
                  winners,
                  isBusiness ? "Bizneset fituese" : "Fituesit e lotarisë",
                  isBusiness
                )
              }
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-[var(--secondary-color)] hover:bg-[var(--secondary-color)]/90 active:scale-[0.98] px-4 py-2 text-sm font-semibold text-white shadow-sm transition flex-1"
            >
              <FiDownload size={14} />
              Shkarko PDF
            </button>
          </div>
          <button
            type="button"
            onClick={handleNavigate}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-[var(--secondary-color)] text-[var(--secondary-color)] hover:bg-[var(--secondary-color)]/5 px-4 py-2 text-sm font-semibold transition w-full"
          >
            Shko te faqja e fituesve
            <FiArrowRight size={14} />
          </button>
        </div>
      </div>
    </Modal>
  );
}
